import { api } from '../../lib/supabase';
import { useApi } from '../../lib/use-api';
import { VerifiedBadge } from '../../components/common/Badges';
import { useState } from 'react';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui/tabs';
import { Loader2, Globe2, Users2, Flag } from 'lucide-react';

export default function Leaderboard() {
  const [tab, setTab] = useState('global');
  const { data: events } = useApi(api.getEvents);
  const [eventId, setEventId] = useState(null);
  const selectedId = eventId || events?.[0]?.id;
  const event = events?.find((e) => e.id === selectedId);

  const { data: globalRows, loading: globalLoading } = useApi(api.getGlobalLeaderboard);
  const { data: friendsRows, loading: friendsLoading } = useApi(api.getFriendsLeaderboard);
  const { data: eventRows, loading: eventLoading } = useApi(api.getLeaderboard, [selectedId]);

  const headlineSubtitle = {
    global: 'Best verified times across every event',
    friends: 'You and your verified friends',
    event: event?.name || 'Pick an event',
  }[tab];

  return (
    <div className="px-4 pt-4 pb-6 space-y-5" data-testid="leaderboard-screen">
      <div>
        <p className="rivl-label">Leaderboard</p>
        <h1 className="font-display text-3xl font-bold tracking-tight mt-1">Who's flying.</h1>
        <p className="text-xs text-muted-foreground mt-1">{headlineSubtitle}</p>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="grid grid-cols-3 h-auto p-1 rounded-md">
          <TabsTrigger value="global" data-testid="lb-tab-global" className="text-xs uppercase tracking-widest gap-1.5">
            <Globe2 className="h-3.5 w-3.5" /> Global
          </TabsTrigger>
          <TabsTrigger value="friends" data-testid="lb-tab-friends" className="text-xs uppercase tracking-widest gap-1.5">
            <Users2 className="h-3.5 w-3.5" /> Friends
          </TabsTrigger>
          <TabsTrigger value="event" data-testid="lb-tab-event" className="text-xs uppercase tracking-widest gap-1.5">
            <Flag className="h-3.5 w-3.5" /> Event
          </TabsTrigger>
        </TabsList>

        <TabsContent value="global" className="mt-4">
          {globalLoading ? <Loader /> : <Table rows={globalRows} emptyText="No verified runs yet." />}
        </TabsContent>

        <TabsContent value="friends" className="mt-4">
          {friendsLoading ? <Loader /> : <Table rows={friendsRows} emptyText="Add some friends to see their times here." />}
        </TabsContent>

        <TabsContent value="event" className="mt-4 space-y-4">
          {events?.length > 1 && (
            <select
              value={selectedId || ''}
              onChange={(e) => setEventId(e.target.value)}
              className="w-full h-10 rounded-md border border-input bg-background px-3 text-sm"
              data-testid="lb-event-select"
            >
              {events.map((e) => (
                <option key={e.id} value={e.id}>
                  {e.name}
                </option>
              ))}
            </select>
          )}
          {eventLoading ? <Loader /> : <Table rows={eventRows} emptyText="No verified finishers yet." />}
        </TabsContent>
      </Tabs>
    </div>
  );
}

function Loader() {
  return <div className="flex justify-center py-10"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>;
}

function Table({ rows, emptyText = 'No verified finishers yet.' }) {
  if (!rows || rows.length === 0) {
    return <p className="text-sm text-muted-foreground py-6 text-center">{emptyText}</p>;
  }
  return (
    <div className="rivl-card divide-y divide-border" data-testid="leaderboard-table">
      {rows.map((row, i) => (
        <div key={`${row.rank}-${i}`} className={`flex items-center gap-3 px-4 py-3 ${row.isYou ? 'bg-primary/5' : ''}`}>
          <div className="font-mono font-bold w-8 text-primary">#{row.rank}</div>
          <div className="flex-1">
            <div className="text-sm font-medium flex items-center gap-2">
              {row.name}
              {row.isYou && <span className="text-[9px] uppercase tracking-widest text-primary">YOU</span>}
            </div>
            <div className="font-mono text-[11px] text-muted-foreground">
              {row.bib ? `Bib ${row.bib} · ` : ''}{row.pace}/km
            </div>
          </div>
          <div className="text-right">
            <div className="font-mono font-bold tabular-nums">{row.time}</div>
            {row.verified && <VerifiedBadge />}
          </div>
        </div>
      ))}
    </div>
  );
}
